import { useMemo } from 'react';
import { formatPLN } from '@/utils/currency';
import { t } from '@/i18n';
import type { InvoiceListItem } from '@ip/shared-types';

interface SelectionSummaryProps {
  /** Selected rows with full data (see useSelectedInvoiceItems). */
  items: InvoiceListItem[];
}

/**
 * Footer sum of the selected invoices. Currencies are never mixed
 * arithmetically: each currency gets its own subtotal ("8 514,00 EUR +
 * 1 230,00 PLN"), and when foreign currencies are present a PLN-equivalent
 * total is appended from the stored NBP conversions (same source as the
 * per-row "≈ PLN" line). Foreign rows that never got a rate are excluded
 * from the PLN total and called out instead of being silently dropped.
 */
export function SelectionSummary({ items }: SelectionSummaryProps) {
  const summary = useMemo(() => {
    const byCurrency = new Map<string, { net: number; gross: number }>();
    let plnNet = 0;
    let plnGross = 0;
    let missingFx = 0;
    let hasForeign = false;
    for (const inv of items) {
      const cur = (inv.currency || 'PLN').toUpperCase();
      const entry = byCurrency.get(cur) || { net: 0, gross: 0 };
      entry.net += inv.netAmount || 0;
      entry.gross += inv.amount || 0;
      byCurrency.set(cur, entry);
      if (cur === 'PLN') {
        plnNet += inv.netAmount || 0;
        plnGross += inv.amount || 0;
      } else {
        hasForeign = true;
        if (inv.netPln != null && inv.grossPln != null) {
          plnNet += inv.netPln;
          plnGross += inv.grossPln;
        } else {
          missingFx++;
        }
      }
    }
    const currencies = [...byCurrency.keys()].sort((a, b) =>
      a === 'PLN' ? -1 : b === 'PLN' ? 1 : a.localeCompare(b),
    );
    return { byCurrency, currencies, plnNet, plnGross, missingFx, hasForeign };
  }, [items]);

  if (items.length === 0) return null;

  const joinAmounts = (kind: 'net' | 'gross') =>
    summary.currencies
      .map((cur) => `${formatPLN(summary.byCurrency.get(cur)![kind])} ${cur}`)
      .join(' + ');

  return (
    <div className="text-right text-xs leading-tight tabular-nums">
      <p className="text-muted-foreground">
        {t('selectionSummary.title', { count: items.length })}{' '}
        {t('selectionSummary.net')}: <span className="font-mono">{joinAmounts('net')}</span>
      </p>
      <p>
        <span className="text-muted-foreground">{t('selectionSummary.gross')}:</span>{' '}
        <span className="font-mono font-semibold">{joinAmounts('gross')}</span>
        {summary.hasForeign && (
          <span className="text-muted-foreground ml-1">
            {t('fx.approxPln', { amount: formatPLN(summary.plnGross) })}
            {summary.missingFx > 0 &&
              ` (${t('selectionSummary.missingRate', { count: summary.missingFx })})`}
          </span>
        )}
      </p>
    </div>
  );
}
